import React, { useCallback, useEffect, useMemo } from "react";

import {
  ArrayUtils,
  getProgramInternalName,
  queryIsLoading,
  useAddOrUpdateProgramParticipation,
  useDeleteProgramParticipation,
  useGetAllCarbonPrograms,
  useGetCarbonProgramContractsForPrograms,
} from "utils";

import { CarbonProgramParticipation, CarbonProgramParticipationRequest } from "models";

import { Col, Form, IconButton, InputNumber, Modal, Option, Row, Select, TextArea, Message as message } from "components";

export type AssignCarbonProgramModalProps = {
  programParticipation: CarbonProgramParticipation | null;
  showModal: boolean;
  onClose: () => void;
};

export const AssignCarbonProgramModal = ({ programParticipation, showModal, onClose }: AssignCarbonProgramModalProps) => {
  const allCarbonProgramsQuery = useGetAllCarbonPrograms();
  const {
    mutate: addOrUpdateProgramParticipation,
    isPending: isPendingAddOrUpdate,
    isSuccess: isSuccessAddOrUpdate,
    isError: isErrorAddOrUpdate,
    reset: resetAddOrUpdate,
  } = useAddOrUpdateProgramParticipation();
  const {
    mutate: deleteProgramParticipation,
    isPending: isPendingDelete,
    isSuccess: isSuccessDelete,
    isError: isErrorDelete,
    reset: resetDelete,
  } = useDeleteProgramParticipation();

  const [form] = Form.useForm();

  const isPending = isPendingAddOrUpdate || isPendingDelete;

  const carbonPrograms = allCarbonProgramsQuery.data ?? [];
  const currentCP = carbonPrograms.find((cp) => cp.id === programParticipation?.carbonProgramId);

  const handleSubmit = (v: Required<Omit<CarbonProgramParticipationRequest, "farmId">>) => {
    if (!programParticipation) {
      return;
    }

    addOrUpdateProgramParticipation({
      farmId: programParticipation.id,
      carbonProgramId: v.carbonProgramId,
      haLimit: +v.haLimit,
      comment: v.comment,
      tags: v.tags.map((tag) => tag.toUpperCase()),
    });
  };

  const handleDeleteClick = () => {
    if (!programParticipation) {
      return;
    }
    deleteProgramParticipation({
      farmId: programParticipation.id,
    });
  };

  // Fetch contracts for all programs to check which have terms and conditions uploaded
  const contractQueries = useGetCarbonProgramContractsForPrograms(
    carbonPrograms.map((p) => p.id),
    { enabled: showModal }
  );

  // Map of programId => { hasContract }
  const programIdToHasContract = useMemo((): Record<string, { hasContract: boolean }> => {
    if (contractQueries.some(queryIsLoading)) return {};
    return ArrayUtils.indexBy(
      contractQueries.map((listOfContracts) => ({ programId: listOfContracts.data?.[0]?.programId ?? "", hasContract: !!listOfContracts.data?.[0] })),
      ({ programId }) => programId
    );
  }, [contractQueries]);

  const handleClose = useCallback(() => {
    resetAddOrUpdate();
    resetDelete();
    onClose();
  }, [resetAddOrUpdate, resetDelete, onClose]);

  useEffect(() => {
    // This resets the initial values when the modal is opened for another farm
    form.resetFields();
  }, [programParticipation?.id, form]);

  useEffect(() => {
    if (isSuccessAddOrUpdate) {
      message({
        content: "The gremlins have successfully added or updated the program",
        icon: "gremlin",
      });

      handleClose();
    }

    if (isSuccessDelete) {
      message({
        content: "The gremlins have successfully deleted the carbon program",
        icon: "gremlin",
      });

      handleClose();
    }

    if (isErrorAddOrUpdate) {
      message({
        content: "There was an error while adding or updating the program",
        icon: "gremlin",
      });
    }

    if (isErrorDelete) {
      message({
        content: "There was an error while deleting the carbon program",
        icon: "gremlin",
      });
    }
  }, [isSuccessAddOrUpdate, isSuccessDelete, isErrorAddOrUpdate, isErrorDelete, handleClose]);

  return (
    <>
      <Modal
        showModal={showModal}
        onCancel={handleClose}
        onOk={() => form.submit()}
        okText="Submit"
        loading={isPending}
        destroyOnHidden={true}
        content={
          <Form form={form} onFinish={handleSubmit}>
            <Row>
              <Col>
                <div style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
                  <div style={{ flex: 1 }}>
                    <Select
                      dataTestId="program-participation-type-select"
                      label={programParticipation?.carbonProgramId ? "Carbon programs currently cannot be changed" : "Select carbon program"}
                      popupMatchSelectWidth={false}
                      name="carbonProgramId"
                      rules={[{ required: true }]}
                      initialValue={programParticipation?.carbonProgramId}
                      showSearch
                    >
                      {carbonPrograms
                        .sort((a, b) => a.programName.localeCompare(b.programName))
                        .map((program, i) => {
                          const hasContract = (programIdToHasContract[program.id]?.hasContract ?? false) || !!program.noProgramTerms;
                          const methodologyDoesDiffer = currentCP && program.accountingMethodology !== currentCP?.accountingMethodology;

                          return (
                            <Option
                              data-testid={`program-participation-type-select-option-${i}`}
                              value={program.id}
                              key={i}
                              disabled={!hasContract || methodologyDoesDiffer}
                            >
                              {getProgramInternalName(program)}
                              {methodologyDoesDiffer && " (methodology differs)"}
                              {!hasContract && " (no T&C uploaded)"}
                            </Option>
                          );
                        })}
                    </Select>
                  </div>
                  {currentCP ? (
                    <div style={{ marginTop: "24px" }}>
                      <IconButton
                        dataTestId="delete-carbon-program-button"
                        iconName="tabler/trash"
                        type="tertiary"
                        size="middle"
                        onClick={handleDeleteClick}
                        tooltip={{ title: "Remove carbon program" }}
                      />
                    </div>
                  ) : null}
                </div>
              </Col>
            </Row>

            <Row>
              <Col>
                <InputNumber
                  dataTestId="program-participation-max-hectares-input"
                  label="Hectare limit"
                  min={0}
                  step={0}
                  name="haLimit"
                  initialValue={programParticipation?.maxHectares}
                />
              </Col>
            </Row>

            <Row>
              <Col>
                <TextArea
                  dataTestId="program-participation-comment-text-area"
                  label="Comment (max. 155)"
                  name="comment"
                  maxLength={155}
                  initialValue={programParticipation?.comment}
                />
              </Col>
            </Row>

            <Row>
              <Col>
                <Select dataTestId="program-participation-tag-select" name="tags" mode="tags" label="Tags" initialValue={programParticipation?.tags ?? []}>
                  {(programParticipation?.tags ?? []).map((tag, index) => (
                    <Option data-testid={`program-participation-tags-option-${index}`} value={tag} key={index}>
                      {tag.toUpperCase()}
                    </Option>
                  ))}
                </Select>
              </Col>
            </Row>
          </Form>
        }
      />
    </>
  );
};
